import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useCalendar } from '../../contexts/CalendarContext';
import './Calendar.css';

const EventModal = ({ isOpen, onClose, event, mode = 'view' }) => {
  const { addEvent, updateEvent, deleteEvent } = useCalendar();
  const [eventData, setEventData] = useState({
    id: '',
    title: '',
    date: new Date(),
    durationInMinutes: 60,
    description: '',
    location: '',
    color: '#4285F4'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (event && mode !== 'create') {
      setEventData({
        ...event,
        date: new Date(event.date)
      });
    } else if (mode === 'create') {
      setEventData({
        ...eventData,
        id: generateId(),
        date: event || new Date()
      });
    }
  }, [event, mode]);

  const generateId = () => {
    return Date.now().toString();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEventData({
      ...eventData,
      [name]: value
    });
  };

  const handleDateChange = (e) => {
    const date = new Date(eventData.date);
    const [hours, minutes] = e.target.value.split(':');
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    
    setEventData({
      ...eventData,
      date
    });
  };

  const handleDurationChange = (e) => {
    setEventData({
      ...eventData,
      durationInMinutes: parseInt(e.target.value, 10)
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!eventData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (mode === 'create') {
      addEvent(eventData);
    } else if (mode === 'edit') {
      updateEvent(eventData);
    }
    
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      deleteEvent(eventData.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{mode === 'create' ? 'Add Event' : mode === 'edit' ? 'Edit Event' : 'Event Details'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {mode === 'view' ? (
            <div className="event-details">
              <h3>{eventData.title}</h3>
              <p><strong>Date:</strong> {format(new Date(eventData.date), 'MMMM d, yyyy')}</p>
              <p><strong>Time:</strong> {format(new Date(eventData.date), 'h:mm a')}</p>
              <p><strong>Duration:</strong> {eventData.durationInMinutes} minutes</p>
              {eventData.location && <p><strong>Location:</strong> {eventData.location}</p>}
              {eventData.description && <p><strong>Description:</strong> {eventData.description}</p>}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={eventData.title}
                  onChange={handleChange}
                  className={errors.title ? 'error' : ''}
                />
                {errors.title && <span className="error-message">{errors.title}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="eventDate">Date</label>
                <input
                  type="date"
                  id="eventDate"
                  name="eventDate"
                  value={format(new Date(eventData.date), 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    newDate.setHours(eventData.date.getHours());
                    newDate.setMinutes(eventData.date.getMinutes());
                    setEventData({
                      ...eventData,
                      date: newDate
                    });
                  }}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="eventTime">Time</label>
                <input
                  type="time"
                  id="eventTime"
                  name="eventTime"
                  value={format(new Date(eventData.date), 'HH:mm')}
                  onChange={handleDateChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="durationInMinutes">Duration (minutes)</label>
                <input
                  type="number"
                  id="durationInMinutes"
                  name="durationInMinutes"
                  min="15"
                  step="15"
                  value={eventData.durationInMinutes}
                  onChange={handleDurationChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={eventData.location}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={eventData.description}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="color">Color</label>
                <input
                  type="color"
                  id="color"
                  name="color"
                  value={eventData.color}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {mode === 'create' ? 'Add Event' : 'Update Event'}
                </button>
                {mode === 'edit' && (
                  <button type="button" className="btn-danger" onClick={handleDelete}>
                    Delete
                  </button>
                )}
                <button type="button" className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
        
        {mode === 'view' && (
          <div className="modal-footer">
            <button onClick={() => onClose('edit')} className="btn-primary">Edit</button>
            <button onClick={handleDelete} className="btn-danger">Delete</button>
            <button onClick={onClose} className="btn-secondary">Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventModal; 